import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
	Input,
	Label,
	Popover,
	PopoverBody,
} from 'reactstrap';
import { v4 as uuid } from 'uuid';

import { messages } from '../../i18n/messages';

import { InfoAlert } from '../Alert';
import Button from '../Button';

import './ShareableLink.scss';

const propTypes = {

	/**
	 * The link URL label.
	 */
	label: PropTypes.string.isRequired,

	/**
	 * The link URL value (if any).
	 */
	url: PropTypes.string,

};

/**
 * Shared component to render a basic TechChange-style component for
 * link URLs that should be shareable with others.
 *
 * @param {Object} props The props.
 *
 * @returns {Object} The rendered component.
 */
const ShareableLink = (props) => {

	const {
		label,
		url,
		...restProps
	} = props;

	// Generate a unique ID for the link component
	const [linkId] = useState(uuid());

	const [isLinkCopied, setIsLinkCopied] = useState(false);

	const linkInputID = `tc-shareable-link-${linkId}-input`;
	const linkPopoverTriggerID = `tc-shareable-link-${linkId}-input-popover-trigger`;
	const linkPopoverContentID = `tc-shareable-link-${linkId}-input-popover-content`;

	const isDisabled = !url;

	const handleCopyLink = () => {

		try {

			const externalLinkInput = document.getElementById(linkInputID);

			externalLinkInput.focus();
			externalLinkInput.select();

			navigator.clipboard.writeText(externalLinkInput.value);

			// Close the alert after 2 seconds
			setTimeout(() => setIsLinkCopied(false), 2000);

		// If execCommand fails or is not supported, let user copy the link themselves
		} catch (error) {

			console.error(error);
			return;

		}

	};

	return (
		<InfoAlert
			{...restProps}
			className={`tc-shareable-link-container ${restProps.className || ''}`}
			dismissible={false}
			prefix={false}>
			<Label for={linkInputID}>
				{label}
			</Label>
			<Input
				id={linkInputID}
				type="text"
				value={url}
				disabled={isDisabled}
				readOnly />
			<div className="d-flex justify-content-end">
				<Button
					id={linkPopoverTriggerID}
					aria-expanded={isLinkCopied}
					className="copy-button"
					color="info"
					type="button"
					disabled={isDisabled}
					onClick={() => handleCopyLink()}>
					<FormattedMessage
						{...messages.TC_COMPONENTS_GENERAL_TEXT_COPY} />
				</Button>
				<Popover
					id={linkPopoverContentID}
					popperClassName="info-tooltip"
					target={linkPopoverTriggerID}
					trigger="click"
					placement="top"
					isOpen={isLinkCopied}
					toggle={() => setIsLinkCopied((prev) => !prev)}>
					<PopoverBody>
						<FormattedMessage
							{...messages.TC_COMPONENTS_GENERAL_TEXT_COPY_SUCCESS} />
					</PopoverBody>
				</Popover>
			</div>
		</InfoAlert>
	);

};

ShareableLink.propTypes = propTypes;

export default ShareableLink;
